import { useFormik } from 'formik';
import React, { useEffect, useState } from 'react';
import { useForeePay } from 'react-foree-pay-checkout';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup'; // Import Yup
import { callGetWebservice, callPostWebservice } from '../hooks/api';
import useDeviceDetails from '../hooks/useDeviceDetails';
import useLocation from '../hooks/useLocation';
import { cartsData, currencyData, isGuestUser, response, userData } from '../utils/constants';
import { getFromLocalStorage, removeFromLocalStorage, saveToLocalStorage } from '../utils/localStorageUtils';
import { getConvertedPrice, getPaymentObject, handleForeeCallback, initiateForeePay, printLogs, validatePhone, getParsedPhone } from '../utils/Utilities';
import StaticPageHeader from '../components/common/StaticPageHeader';

// Import Components
import BillingAddress from '../components/checkout/BillingAddress';
import OrderNotes from '../components/checkout/OrderNotes';
import OrderSummary from '../components/checkout/OrderSummary';
import PaymentMethods from '../components/checkout/PaymentMethods';
import ShippingAddress from '../components/checkout/ShippingAddress';
import ProgressBar from '../components/common/ProgressBar';
import { setCartCount } from '../store/reducers/auth';
import ContactInformation from '../components/checkout/ContactInformation';
import useMerchantSettings from '../hooks/useMerchantSettings';



const Checkout = () => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const deviceDetails = useDeviceDetails();

    const [total, setTotal] = useState(null);
    const [carts, setCarts] = useState(getFromLocalStorage(cartsData) || []);
    const [checkoutData, setCheckoutData] = useState(null);
    const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
    const [couponCode, setCouponCode] = useState('');
    const [couponData, setCouponData] = useState(null);

    const merConfiguration = useMerchantSettings();
    let config = merConfiguration?.merchantSettings.config;

    const [merchantSettings, setMerchantSettings] = useState(null);
    const [loading, setLoading] = useState(true);

    const { executeForeePay } = useForeePay();

    // Fetch and Parse Country , State and City
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);

    const [showState, setShowState] = useState(false);
    const [showCity, setShowCity] = useState(false);
    // End Fetch and Parse Country , State and City 

    const [billingAddressOption, setBillingAddressOption] = useState("same");
    const [xPay, setXPayData] = useState(null);

    let currency = getFromLocalStorage(currencyData);

    // Add state for error messages
    const [errorMessage, setErrorMessage] = useState('');

    // Add state for form errors
    const [formError, setFormError] = useState(null);

    const contactValidationSchema = React.useMemo(() => Yup.object().shape({
        contactName: Yup.string().required('Name is required'),
        contactEmail: Yup.string().email('Invalid email address').required('Email is required'),
        contactCountry: Yup.string().required('Country is required'),
        contactPhone: Yup.string()
            .required('Phone number is required')
            .test('is-valid-phone', 'Use a national or international number valid for the selected country.', function (value) {
                const { contactCountry } = this.parent;
                if (!contactCountry || !value) return true;
                const country = countries.find(c => c.id === contactCountry);
                if (country) {
                    return validatePhone(value, country.iso2);
                }
                return true;
            }),
    }), [countries]);

    const shippingValidationSchema = React.useMemo(() => Yup.object().shape({
        shippingName: Yup.string().required('Full Name is required'),
        shippingMobileNumber: Yup.string()
            .required('Mobile Number is required')
            .test('is-valid-phone', 'Use a national or international number valid for the selected country.', function (value) {
                const { shippingCountry } = this.parent;
                if (!shippingCountry || !value) return true;
                const country = countries.find(c => c.id === shippingCountry);
                if (country) {
                    return validatePhone(value, country.iso2);
                }
                return true;
            }),
        shippingAddress: Yup.string().required('Address is required'),
        shippingCountry: Yup.string().required('Country is required'),
        shippingState: Yup.string().required('State is required'),
        shippingCity: Yup.string().required('City is required'),
        shippingZip: Yup.string().required('ZIP code is required'),
    }), [countries]);

    useEffect(() => {
        if (carts.length === 0) {
            navigate('/');
        }
        else {
            // if(!isLoggedIn)
            //     navigate('/login');
        }
        calculatePrice();
    }, [carts, couponData, config]);

    useEffect(() => {
        if (couponCode.length !== 0)
            calculatePrice();
    }, [couponData]);

    const calculateTax = (amount, rate) => {
        return (parseFloat(amount) * parseFloat(rate)) / 100;
    };

    const calculatePrice = () => {

        let subTotal = 0;

        carts.forEach((cart, index) => {
            let quantity = cart.quantity;
            let variant = cart.selected_variant;

            subTotal += getConvertedPrice(variant.sale_price) * quantity;
        });

        let total = subTotal;
        let gstPer = 0;
        let gst = 0;
        let deliveryCharges = 0;
        let couponValue = 0;
        let couponId = 0;
        let tCouponCode = null;

        if (checkoutData != null) {
            if (checkoutData.gst) {
                gstPer = checkoutData?.gst.gst_percentage;
                let calGST = ((subTotal * gstPer) / 100);
                gst = parseFloat(calGST);
                total += gst;
            }

            if (checkoutData.deliveryCharges) {
                let cDeliveryCharges = checkoutData.deliveryCharges;
                deliveryCharges = getConvertedPrice(cDeliveryCharges.delivery_charges);
                let zeroCharges = getConvertedPrice(cDeliveryCharges.zero_charges);
                if (subTotal.toFixed(2) >= zeroCharges) {
                    deliveryCharges = 0;
                }

                total += deliveryCharges;
            }
        }

        if (couponData) {
            let { min_order_value: minOrderValue, value, type, id } = couponData.coupon;
            if (!minOrderValue || subTotal > getConvertedPrice(minOrderValue)) {
                couponValue = type === 'Fixed' ? value : ((subTotal * value) / 100);
                total = Math.max(0, total - couponValue);
                tCouponCode = couponCode;
                couponId = id;
            }
        }

        let incomeTax = 0;
        let incomeTaxRate = 0;
        let salesTax = 0;
        let salesTaxRate = 0;

        if (config) {
            if (config.income_tax_enabled === 1) {
                incomeTaxRate = config.income_tax_rate;
                incomeTax = calculateTax(subTotal, incomeTaxRate);
                total += incomeTax;
            }

            if (config.sales_tax_enabled === 1) {
                salesTaxRate = config.sales_tax_rate;
                salesTax = calculateTax(subTotal, salesTaxRate);
                total += salesTax;
            }
        }

        setTotal({
            subTotal: subTotal?.toFixed(2),
            total: total?.toFixed(2),
            currency: currency?.currency.symbol,
            deliveryCharges: deliveryCharges,
            gst: gst.toFixed(2),
            gstPer: gstPer,
            coupon: couponValue.toFixed(2),
            couponCode: tCouponCode,
            couponId: couponId,
            incomeTax: incomeTax.toFixed(2),
            incomeTaxRate: incomeTaxRate,
            incomeTaxEnabled: config?.income_tax_enabled === 1,
            salesTax: salesTax.toFixed(2),
            salesTaxRate: salesTaxRate,
            salesTaxEnabled: config?.sales_tax_enabled === 1,
        });

        if (couponCode) {
            setCouponCode('');
        }
    };

    const validatePlaceOrder = () => {

        let result = true;
        if (checkoutData != null) {
            let deliveryCharges = checkoutData?.deliveryCharges;
            if (deliveryCharges) {
                if (deliveryCharges.is_delivery_available === 0) {
                    result = false;
                    setFormError('Delivery is not available in your city');
                }
                else if (total.total < deliveryCharges.min_order) {
                    result = false;
                    setFormError(`Minimum order to place is ${total.currency} ${deliveryCharges.min_order}`);
                }
            }

        }

        return result;
    }

    useEffect(() => {
        callGetWebservice('countries', {}, (response) => {
            setCountries(response.data);
        });

        callGetWebservice('merchant-settings', {}, (response) => {
            setMerchantSettings(response.data);
        });

        if (!isLoggedIn) {
            setLoading(false);
            return;
        }

        let currency = getFromLocalStorage(currencyData);

        let reqPacket = { currency_id: currency?.currency.id };
        callGetWebservice('checkout-address', reqPacket, async (response) => {
            let address = response.data.address;
            setLoading(false);
            if (address) {
                let billing = address.filter((i) => i.type === 'Billing')[0];
                let shipping = address.filter((i) => i.type === 'Shipping')[0];

                if (billing || shipping) {
                    formik.setValues({
                        ...formik.values,
                        shippingName: shipping?.name || '',
                        shippingMobileNumber: shipping?.phone_number || '',
                        shippingAddress: shipping?.address || '',
                        shippingCountry: shipping?.country_id || '',
                        shippingState: shipping?.state_id || '',
                        shippingCity: shipping?.city_id || '',
                        shippingZip: shipping?.zip_code || '',
                        shippingCountryName: shipping?.countryName || '',
                        shippingStateName: shipping?.stateName || '',
                        shippingCityName: shipping?.cityName || '',
                        additionalInfo: '',
                        billingSameAsShipping: billingAddressOption === 'same' ? true : false,

                        billingName: billing?.name || shipping?.name,
                        billingMobileNumber: billing?.phone_number || shipping?.phone_number,
                        billingAddress: billing?.address || shipping?.address,
                        billingCountry: billing?.country_id || shipping?.country_id,
                        billingState: billing?.state_id || shipping?.state_id,
                        billingCity: billing?.city_id || shipping?.city_id,
                        billingZip: billing?.zip_code || shipping?.zip_code,
                        billingCountryName: billing?.countryName || shipping?.countryName || '',
                        billingStateName: billing?.stateName || shipping?.stateName || '',
                        billingCityName: billing?.cityName || shipping?.cityName || '',
                    });

                    if (shipping) {
                        callCheckoutSettings(shipping?.country_id, shipping?.state_id, shipping?.city_id);
                    }
                }
            }
        }, (error) => {
            setLoading(false);
        }, dispatch, navigate);
    }, []);

    const callCheckoutSettings = (countryId = null, stateId = null, cityId = null) => {

        if ((!countryId || countryId === "") &&
            (!stateId || stateId === "") &&
            (!cityId || cityId === "")) {
            printLogs("All parameters are null, empty, or undefined. API call will not be made.");
            return;
        }

        let currency = getFromLocalStorage(currencyData);

        let requestPacket =
        {
            currency_id: currency.currency.id,
            country_id: countryId ?? null,
            state_id: stateId ?? null,
            city_id: cityId ?? null,
        };

        callGetWebservice('checkout-settings', requestPacket, (response) => {
            setCheckoutData(response.data);
        }, null, dispatch, navigate);
    }

    useEffect(() => {
        calculatePrice();
    }, [checkoutData]);

    // Fetch and Parse Country , State and City

    const handleCustomerCountryChange = (input) => {
        const countryId = input.value;
        formik.setFieldValue("contactCountry", countryId);
    }

    const handleShippingCountryChange = (input) => {
        const countryId = input.value;
        formik.setFieldValue("shippingCountry", countryId);

        callGetWebservice('states', { country_id: countryId }, (response) => {
            setStates(response.data);
            setShowState(true);
            setCities([]);
            setShowCity(false);
        }, null, dispatch, navigate);
    };

    const handleShippingStateChange = (input) => {
        const stateId = typeof input === "object" ? input.target.value : input;
        formik.setFieldValue("shippingState", stateId);

        callGetWebservice('cities', { state_id: stateId }, (response) => {
            setCities(response.data);
            setShowCity(true);

        }, null, dispatch, navigate);
    };

    const handleShippingCityChange = (input) => {
        const cityId = typeof input === "object" ? input.target.value : input;
        formik.setFieldValue("shippingCity", cityId);

        if (formik.values.shippingCountry && formik.values.shippingState && cityId) {
            callCheckoutSettings(formik.values.shippingCountry, formik.values.shippingState, cityId);
        }

        callGetWebservice('areas', { city_id: cityId }, (response) => {
        }, null, dispatch, navigate);
    };

    const handleBillingCountryChange = (input) => {
        const countryId = input.value;
        formik.setFieldValue("billingCountry", countryId);

        callGetWebservice('states', { country_id: countryId }, (response) => {
            setStates(response.data);
            setShowState(true);
            setCities([]);
            setShowCity(false);
        }, null, dispatch, navigate);
    };

    const handleBillingStateChange = (input) => {
        const stateId = typeof input === "object" ? input.target.value : input;
        formik.setFieldValue("billingState", stateId);

        callGetWebservice('cities', { state_id: stateId }, (response) => {
            setCities(response.data);
            setShowCity(true);

        }, null, dispatch, navigate);
    };

    const handleBillingCityChange = (input) => {
        const cityId = typeof input === "object" ? input.target.value : input;
        formik.setFieldValue("billingCity", cityId);

        callGetWebservice('areas', { city_id: cityId }, (response) => {
        }, null, dispatch, navigate);
    };

    // End Fetch and Parse Country , State and City

    // Merge schemas based on login status
    const getValidationSchema = () => {
        if (!isLoggedIn) {
            return Yup.object().shape({
                ...contactValidationSchema.fields,
                ...shippingValidationSchema.fields,
                payment_id: Yup.string()
                    .required('Please select a payment method'),
                // Add conditional billing validation
                ...(billingAddressOption === 'different' && {
                    billingName: Yup.string().required('Billing Name is required'),
                    billingMobileNumber: Yup.string().required('Billing Mobile Number is required'),
                    billingAddress: Yup.string().required('Billing Address is required'),
                    billingCountry: Yup.string().required('Billing Country is required'),
                    billingState: Yup.string().required('Billing State is required'),
                    billingCity: Yup.string().required('Billing City is required'),
                    billingZip: Yup.string().required('Billing ZIP code is required'),
                })
            });
        }

        return Yup.object().shape({
            ...shippingValidationSchema.fields,
            payment_id: Yup.string()
                .required('Please select a payment method'),
            // Add conditional billing validation
            ...(billingAddressOption === 'different' && {
                billingName: Yup.string().required('Billing Name is required'),
                billingMobileNumber: Yup.string().required('Billing Mobile Number is required'),
                billingAddress: Yup.string().required('Billing Address is required'),
                billingCountry: Yup.string().required('Billing Country is required'),
                billingState: Yup.string().required('Billing State is required'),
                billingCity: Yup.string().required('Billing City is required'),
                billingZip: Yup.string().required('Billing ZIP code is required'),
            })
        });
    };

    const formik = useFormik({
        initialValues: {
            err: '',
            shippingName: '',
            shippingMobileNumber: '',
            shippingAddress: '',
            shippingCountry: '',
            shippingState: '',
            shippingCity: '',
            shippingZip: '',
            shippingCountryName: '',
            shippingStateName: '',
            shippingCityName: '',
            additionalInfo: '',
            billingSameAsShipping: billingAddressOption === 'same',
            billingName: '',
            billingMobileNumber: '',
            billingAddress: '',
            billingCountry: '',
            billingState: '',
            billingCity: '',
            billingZip: '',
            billingCountryName: '',
            billingStateName: '',
            billingCityName: '',
            payment_id: '',
            contactName: '',
            contactEmail: '',
            contactPhone: '',
            contactCountry: '',
        },
        validationSchema: getValidationSchema(),
        validateOnMount: false,
        validateOnChange: false,
        validateOnBlur: true,
        enableReinitialize: true,
        context: { merchantSettings },  // Add context for merchant settings
        onSubmit: (values, { setSubmitting }) => {
            try {
                setFormError(null);

                // Validation will be handled automatically by Formik
                // based on the merged schema

                // Rest of your submit logic...
                const tokenData = getFromLocalStorage(userData);
                const paymentObject = getPaymentObject(merchantSettings, values.payment_id);

                if (!paymentObject) {
                    setFormError('Please select a valid payment method');
                    setSubmitting(false);
                    return;
                }

                if (!validatePlaceOrder()) {
                    setSubmitting(false);
                    return;
                }

                const selectedShipCountry = countries.find((c) => String(c.id) === String(values.shippingCountry))?.name || values.shippingCountryName || '';
                const selectedShipState = states.find((s) => String(s.id) === String(values.shippingState))?.name || values.shippingStateName || '';
                const selectedShipCity = cities.find((c) => String(c.id) === String(values.shippingCity))?.name || values.shippingCityName || '';

                const isSameBilling = billingAddressOption === 'same';
                const selectedBillCountry = isSameBilling
                    ? selectedShipCountry
                    : countries.find((c) => String(c.id) === String(values.billingCountry))?.name || values.billingCountryName || '';
                const selectedBillState = isSameBilling
                    ? selectedShipState
                    : states.find((s) => String(s.id) === String(values.billingState))?.name || values.billingStateName || '';
                const selectedBillCity = isSameBilling
                    ? selectedShipCity
                    : cities.find((c) => String(c.id) === String(values.billingCity))?.name || values.billingCityName || '';

                let postData = {
                    bill_name: values.billingName,
                    bill_email: !isLoggedIn ? values.contactEmail : tokenData?.email,
                    bill_mobile: values.billingMobileNumber,
                    bill_zipcode: values.billingZip,
                    bill_address: values.billingAddress,

                    bill_country_id: values.billingCountry,
                    bill_state_id: values.billingState,
                    bill_city_id: values.billingCity,

                    bill_country: selectedBillCountry,
                    bill_state: selectedBillState,
                    bill_city: selectedBillCity,

                    ship_name: values.shippingName,
                    ship_email: !isLoggedIn ? values.contactEmail : tokenData?.email,
                    ship_mobile: values.shippingMobileNumber,
                    ship_zipcode: values.shippingZip,
                    ship_address: values.shippingAddress,

                    ship_country_id: values.shippingCountry,
                    ship_state_id: values.shippingState,
                    ship_city_id: values.shippingCity,

                    ship_country: selectedShipCountry,
                    ship_state: selectedShipState,
                    ship_city: selectedShipCity,

                    has_billing_shipping: true,

                    order_notes: values.additionalInfo,
                    has_prepayment: merchantSettings?.has_prepayment,
                    products: carts,
                    currency_id: currency?.currency.id,
                    payment_id: paymentObject.payment_id,
                    payment_gateway: paymentObject.display_name,
                    instance_id: paymentObject.instance_id,
                    latitude: location?.latitude,
                    longitude: location?.longitude,

                    user_agent: deviceDetails.userAgent,
                    channel: deviceDetails.channel,
                    request_type: 'Website',
                    order_type: 'Online',
                    browser: deviceDetails.browser,
                    device: deviceDetails.device,
                    os: deviceDetails.os,
                    manufacturer: deviceDetails.manufacturer,
                    model: deviceDetails.model,
                    platform: deviceDetails.platform,
                }

                if (total.couponId) {
                    postData.coupon_id = total.couponId
                }

                let url = 'orders';
                if (!isLoggedIn) {
                    const contactCountryObj = countries.find(c => c.id === values.contactCountry);
                    const parsedContactPhone = contactCountryObj ? getParsedPhone(values.contactPhone, contactCountryObj.iso2) : null;
                    const formattedContactPhone = parsedContactPhone ? parsedContactPhone.number : values.contactPhone;

                    postData.contact_name = values.contactName;
                    postData.contact_email = values.contactEmail;
                    postData.contact_mobile = formattedContactPhone;
                    postData.contact_country = values.contactCountry;
                    postData.is_guest_user = true;
                    url = 'guest/orders';
                }
                else {
                    postData.is_guest_user = false;
                }

                saveToLocalStorage(isGuestUser, postData.is_guest_user);

                printLogs('checkout postData', postData);


                callPostWebservice(url, postData, (response) => {
                    setSubmitting(false);
                    const data = response.data;

                    if (!data.is_order_success) {
                        navigateToCompleteOrder(data);
                        return;
                    }

                    if (response.data.has_redirect) {

                        if (response.data.key === 'foreepay') {
                            callForePay(response.data);
                        }
                        else if (response.data.key === 'card') {
                            const paymentObject = getPaymentObject(merchantSettings, values.payment_id);

                            setXPayData({
                                api_key: response.data.api_key,
                                account_id: response.data.account_id,
                                hmac_secret: response.data.hmac_secret,
                                slug: response.data.slug,
                                order_ref: response.data.order_ref,
                                instance_id: paymentObject.instance_id,
                                order_date: response.data.created_at,
                                order_message : response.data.order_message,
                                merchant_id : response.data.merchant_id
                            });
                        }
                        else {
                            window.location.href = response.data.url;
                        }
                    }
                    else {
                        navigateToCompleteOrder(data);
                    }

                }, (error) => {
                    setSubmitting(false);
                    setFormError(error.errMessage || 'Something went wrong. Please try again.');
                }, dispatch, navigate);
            } catch (error) {
                console.error('Form submission error:', error);
                setFormError('An error occurred while processing your order. Please try again.');
                setSubmitting(false);
            }
        },
    });

    // Update billingSameAsShipping when billingAddressOption changes
    useEffect(() => {
        const currentValues = { ...formik.values };

        if (billingAddressOption === 'same') {
            // When checking "same as shipping", sync billing with shipping
            formik.setValues({
                ...currentValues,
                billingSameAsShipping: true,
                billingName: currentValues.shippingName,
                billingMobileNumber: currentValues.shippingMobileNumber,
                billingAddress: currentValues.shippingAddress,
                billingCountry: currentValues.shippingCountry,
                billingState: currentValues.shippingState,
                billingCity: currentValues.shippingCity,
                billingZip: currentValues.shippingZip,
                billingCountryName: currentValues.shippingCountryName,
                billingStateName: currentValues.shippingStateName,
                billingCityName: currentValues.shippingCityName,
            }, false);
        } else {
            // When unchecking, only clear billing fields and update billingSameAsShipping flag
            formik.setValues({
                ...currentValues,
                billingSameAsShipping: false,
                billingName: '',
                billingMobileNumber: '',
                billingAddress: '',
                billingCountry: '',
                billingState: '',
                billingCity: '',
                billingZip: '',
                billingCountryName: '',
                billingStateName: '',
                billingCityName: '',
            }, false);
        }
    }, [billingAddressOption]);

    // Keep billing address in sync with shipping when billingSameAsShipping is true
    useEffect(() => {
        if (billingAddressOption === 'same') {
            const currentValues = { ...formik.values };
            formik.setValues({
                ...currentValues,
                billingName: currentValues.shippingName,
                billingMobileNumber: currentValues.shippingMobileNumber,
                billingAddress: currentValues.shippingAddress,
                billingCountry: currentValues.shippingCountry,
                billingState: currentValues.shippingState,
                billingCity: currentValues.shippingCity,
                billingZip: currentValues.shippingZip,
                billingCountryName: currentValues.shippingCountryName,
                billingStateName: currentValues.shippingStateName,
                billingCityName: currentValues.shippingCityName,
            }, false);
        }
    }, [
        billingAddressOption,
        formik.values.shippingName,
        formik.values.shippingMobileNumber,
        formik.values.shippingAddress,
        formik.values.shippingCountry,
        formik.values.shippingState,
        formik.values.shippingCity,
        formik.values.shippingZip
    ]);

    const btnApplyCouponClick = () => {
        if (!couponCode.trim()) {
            setErrorMessage('Please enter a coupon code');
            return;
        }

        let requestPacket = {
            code: couponCode,
        }

        callGetWebservice('coupon', requestPacket, (response) => {
            setCouponData(response.data);
            setErrorMessage('');
        }, (error) => {
            setErrorMessage(error.message || 'Invalid coupon code');
            setCouponData(null);
        }, dispatch, navigate);
    }

    // printLogs('formik', { err: formik.errors, va: formik.values, sub: formik.isSubmitting });
    // printLogs('total', { total })

    // Foree Pay
    const callForePay = (data) => {
        initiateForeePay(data, saveToLocalStorage, response, executeForeePay, foreeCallbackFunc);
    }

    const foreeCallbackFunc = (responseJsonObject = null) => {

        handleForeeCallback(responseJsonObject, getFromLocalStorage, removeFromLocalStorage, response, callGetWebservice, (response, originalResponse) => {

            let responseData = response.data;
            let isSuccessResponse = responseData.success === 1 ? true : false;

            if (isSuccessResponse) {
                removeFromLocalStorage(cartsData);
                dispatch(setCartCount(0));
            }

            printLogs('forepay response', response);
            navigate('/completeorder', { state: { order_id: responseData.order_ref, success: isSuccessResponse, created_at: responseData.created_at, message: originalResponse.message } });
        });
    }

    // Update shouldShowXPay to be a value instead of a function
    const shouldShowXPay = formik.values.payment_id &&
        getPaymentObject(merchantSettings, formik.values.payment_id)?.key === 'card' &&
        xPay;

    useEffect(() => {
        const paymentObject = getPaymentObject(merchantSettings, formik.values.payment_id);
        if (paymentObject?.key !== 'card') {
            setXPayData(null);
        }
    }, [formik.values.payment_id, merchantSettings]);

    // Modify handleFormSubmit to include better error handling
    const handleFormSubmit = async () => {
        // Touch all relevant fields based on billing address option
        const fieldsToTouch = [
            'shippingName', 'shippingMobileNumber', 'shippingAddress', 'shippingCountry', 'shippingState', 'shippingCity', 'shippingZip'
        ];

        if (!isLoggedIn) {
            fieldsToTouch.push(
                'contactName', 'contactEmail', 'contactPhone',
                'contactCountry'
            );
        }

        if (billingAddressOption !== 'same') {
            fieldsToTouch.push(
                'billingName', 'billingMobileNumber', 'billingAddress',
                'billingCountry', 'billingState', 'billingCity', 'billingZip'
            );
        }

        fieldsToTouch.push(
            'payment_id'
        );

        fieldsToTouch.forEach(field => {
            formik.setFieldTouched(field, true);
        });

        // Validate the form
        const errors = await formik.validateForm();

        if (Object.keys(errors).length > 0) {


            // Group errors by section
            const shippingErrors = Object.keys(errors).filter(key => key.startsWith('shipping'));
            const contactErrors = Object.keys(errors).filter(key => key.startsWith('contact'));
            const billingErrors = Object.keys(errors).filter(key => key.startsWith('billing'));
            const paymentErrors = Object.keys(errors).filter(key => key.startsWith('payment'));

            let errorMessage = '';

            if (paymentErrors.length > 0) {
                errorMessage += 'Please select a payment method';
            }
            else if (contactErrors.length > 0) {
                errorMessage += 'Contact Information: Please fill in all required fields';
            }
            else if (shippingErrors.length > 0 && billingErrors.length > 0) {
                errorMessage += 'Billing and Shipping Address: Please fill in all required fields';
            }
            else {
                if (shippingErrors.length > 0) {
                    errorMessage += 'Shipping Address: Please fill in all required fields\n';
                }
                if (billingErrors.length > 0 && billingAddressOption !== 'same') {
                    errorMessage += 'Billing Address: Please fill in all required fields';
                }
            }

            setFormError(errorMessage);
            return;
        }

        // If validation passes, submit the form
        formik.handleSubmit();
    };

    const navigateToCompleteOrder = (data) => {

        const isOrderSuccess = data.is_order_success;
        if (isOrderSuccess) {
            removeFromLocalStorage(cartsData);
            dispatch(setCartCount(0));
        }

        navigate('/completeorder', { state: { order_id: data.order_ref, success: data.is_order_success, created_at: data.created_at, message: data.order_message } });
    }

    return (
        <div className="static-page-wrapper">
            <StaticPageHeader title="Checkout" />
            <section className="checkout-section container static-page-container mt-4">
                <div className="static-page-content" style={{ padding: '30px', backgroundColor: 'transparent', boxShadow: 'none' }}>
                {formError && (
                    <div className="checkout-error-alert mb-4">
                        <div className="error-content">
                            <i className="fas fa-exclamation-circle"></i>
                            <p>{formError}</p>
                            <button
                                className="close-btn"
                                onClick={() => setFormError(null)}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    </div>
                )}

                <div className="row">
                    {/* Left Side - Checkout Form */}
                    <div className="col-lg-7 col-md-12">
                        <div className="checkout-steps">

                            {!isLoggedIn && <ContactInformation
                                formik={formik}
                                countries={countries}
                                handleCustomerCountryChange={handleCustomerCountryChange}
                            />}


                            <ShippingAddress
                                formik={formik}
                                countries={countries}
                                states={states}
                                cities={cities}
                                showState={showState}
                                showCity={showCity}
                                handleShippingCountryChange={handleShippingCountryChange}
                                handleShippingStateChange={handleShippingStateChange}
                                handleShippingCityChange={handleShippingCityChange}
                            />

                            {/* Billing Address Option */}
                            <div className="billing-option mt-4 ml-4">
                                <label className="billing-toggle">
                                    <input
                                        type="checkbox"
                                        checked={billingAddressOption === "same"}
                                        onChange={(e) => setBillingAddressOption(e.target.checked ? "same" : "different")}
                                    />
                                    <span className="ms-2 ml-2">Billing address same as shipping</span>
                                </label>
                            </div>

                            {billingAddressOption === "different" && (
                                <BillingAddress
                                    formik={formik}
                                    countries={countries}
                                    states={states}
                                    cities={cities}
                                    showState={showState}
                                    showCity={showCity}
                                    handleBillingCountryChange={handleBillingCountryChange}
                                    handleBillingStateChange={handleBillingStateChange}
                                    handleBillingCityChange={handleBillingCityChange}
                                />
                            )}

                            <PaymentMethods
                                loading={loading}
                                merchantSettings={merchantSettings}
                                formik={formik}
                                shouldShowXPay={shouldShowXPay}
                                xPay={xPay}
                            />

                            <OrderNotes formik={formik} />
                        </div>
                    </div>

                    {/* Right Side - Order Summary */}
                    <div className="col-lg-5 col-md-12">
                        <OrderSummary
                            carts={carts}
                            currency={currency}
                            total={total}
                            couponCode={couponCode}
                            setCouponCode={setCouponCode}
                            errorMessage={errorMessage}
                            setErrorMessage={setErrorMessage}
                            couponData={couponData}
                            setCouponData={setCouponData}
                            btnApplyCouponClick={btnApplyCouponClick}
                            formik={formik}
                            handleFormSubmit={handleFormSubmit}
                            xPay={xPay}
                        />
                    </div>
                    </div>
                </div>
            </section>
            <ProgressBar submitting={formik.isSubmitting} message="Placing your order..." />
        </div>
    );
};

export default Checkout